import { Siteswap, VanillaSiteswap } from 'universal-siteswap';
import Event from './Event.js';
import Juggler from './Juggler.js';
import Rhythm from './Rhythm.js';

export default class Pattern {
  constructor() {
    this.numBeats = 0;
    this.jugglers = [];
    this.events = [];
  }

  get numJugglers() { return this.jugglers.length; }

  toJIF() {
    const jifJugglers = this.jugglers.map(juggler => juggler.toJIF(this.numJugglers));
    const jifLimbs = this.jugglers.flatMap(juggler => juggler.jifLimbs());
    const jifThrows = this.events.map(event => event.toJIFThrow(jifLimbs));
    const jif = {
      meta: {
        version: "0.01",
        name: "",
        type: "general",
        description: "JIF",
        generator: "Causal Editor",
      },
      timeStretchFactor: 1,
      jugglers: jifJugglers,
      limbs: jifLimbs,
      throws: jifThrows,
      repetition: {
        period: this.numBeats,
      },
    };

    return jif;
  }

  fromJIF(jif) {
    this.clearPattern();
    this.numBeats = jif.repetition.period;
    const timeStretchFactor = jif.timeStretchFactor || 1;

    for (const event of jif.throws) {
      const hasThrow = event.duration !== undefined;
      this.events.push(new Event({
        time: event.time / timeStretchFactor,
        juggler: jif.limbs[event.from].juggler,
        hand: jif.limbs[event.from].type == "right hand" ? 1 : 0,
        duration: hasThrow ? event.duration / timeStretchFactor : undefined,
        toJuggler: hasThrow ? jif.limbs[event.to].juggler : undefined,
        toHand: hasThrow ? (jif.limbs[event.to].type == "right hand" ? 1 : 0) : undefined,
      }));
    }

    for (let i = 0; i < jif.jugglers.length; i++) {
      this.jugglers.push(Juggler.fromJIF(i, jif.jugglers[i], this.events));
    }
    this.events.sort((a, b) => a.cmp(b));
    this.updateEventLinks();
  }

  fromSiteswap(siteswap) {
    if (siteswap === "") {
      return;
    }
    try {
      const vs = VanillaSiteswap.Parse(siteswap);
      this.fromJIF(Siteswap.FromKHSS(vs, this.numJugglers * 2).toJIF());
    } catch (error) {
      try {
        const ss = Siteswap.Parse(siteswap);
        this.fromJIF(ss.toJIF());
      } catch (error) {
        console.error("Failed to parse siteswap: " + siteswap);
        console.log(error);
        return error;
      }
    }
  }

  toSiteswap() {
    return Siteswap.FromJif(this.toJIF()).toString();
  }

  addBeat() {
    this.numBeats++;
    this.jugglers.forEach(juggler => this.addMissingEvents(juggler.id));
    this.updateEventLinks();
  }

  removeBeat() {
    if (this.numBeats === 0) return;
    this.numBeats--;
    this.removeEvents(event => event.time >= this.numBeats);
  }

  addJuggler(name, position, lookAt, rhythm, delay) {
    const jugglerRhythm = rhythm ? rhythm : this.numJugglers === 0 ? Rhythm.ASYNC_RH : this.jugglers[this.numJugglers - 1].rhythm;
    this.jugglers.push(new Juggler({
      id: this.numJugglers,
      name: name,
      delay: delay || 0,
      rhythm: jugglerRhythm,
      position: position,
      lookAt: lookAt,
    }));
    this.addMissingEvents(this.numJugglers - 1);
    this.updateEventLinks();
  }

  removeJuggler() {
    if (this.numJugglers === 0) return;
    this.jugglers.pop();
    this.removeEvents(event => event.juggler === this.numJugglers);
  }

  addThrow(fromEvent, toEvent) {
    fromEvent.addThrow(toEvent, this.numBeats);
    this.updateEventLinks();
  }

  removeThrow(fromEvent) {
    fromEvent.removeThrow();
    this.updateEventLinks();
  }

  addMissingEvents(juggler) {
    const rhythm = this.jugglers[juggler].rhythm;
    const delay = this.jugglers[juggler].delay;
    const seen = [{}, {}];
    for (const event of this.events.filter(event => event.juggler == juggler)) {
      seen[event.hand][event.time.toFixed(3)] = true;
    }
    if (rhythm.isSync()) {
      const delta = rhythm.isSyncStandard() ? 2 : 1;
      for (let time = delay; time < this.numBeats; time += delta) {
        if (undefined == seen[0][time.toFixed(3)]) {
          this.events.push(new Event({time, juggler, hand:0}));
        }
        if (undefined == seen[1][time.toFixed(3)]) {
          this.events.push(new Event({time, juggler, hand:1}));
        }
      }
    } else if (rhythm.isAsync()) {
      const initialHand = rhythm.isAsyncRH() ? 1 : 0;
      for (let beat = 0; beat < this.numBeats; beat++) {
        const time = beat + delay;
        const hand = initialHand ^ (beat % 2);
        if (undefined == seen[hand][time.toFixed(3)]) {
          this.events.push(new Event({time, juggler, hand}));
        }
      }
    }
    this.events.sort((a, b) => a.cmp(b));
  }

  setJugglerDelay(jugglerIndex, delay) {
    if (jugglerIndex < 0 || jugglerIndex >= this.numJugglers) {
      throw new Error("Invalid juggler index");
    }
    const delayDelta = delay - this.jugglers[jugglerIndex].delay;
    this.jugglers[jugglerIndex].delay = delay;
    for (const event of this.events) {
      if (event.juggler == jugglerIndex) {
        event.time += delayDelta;
        if (event.toJuggler != jugglerIndex && event.hasThrow()) {
          event.duration -= delayDelta;
        }
      } else if (event.toJuggler == jugglerIndex) {
        event.duration += delayDelta
      }
    }
  }

  setJugglerRhythm(jugglerIndex, rhythm) {
    if (jugglerIndex < 0 || jugglerIndex >= this.numJugglers) {
      throw new Error("Invalid juggler index");
    }
    if (!(rhythm instanceof Rhythm)) {
      rhythm = new Rhythm(rhythm);
    }
    this.jugglers[jugglerIndex].rhythm = rhythm;
    const delay = this.jugglers[jugglerIndex].delay;
    const initialHand = rhythm.isAsyncRH() ? 1 : 0;
    if (rhythm.isSyncStandard()) {
      const toRemove = this.events.filter(event => event.juggler == jugglerIndex && event.time % 2 != delay);
      toRemove.forEach(event => this.removeEvent(event));
    } else {
      const toRemove = this.events.filter(event => event.juggler == jugglerIndex && event.time % 1 != delay);
      toRemove.forEach(event => this.removeEvent(event));
    }
    for (let beat = 0; beat < this.numBeats; beat++) {
      const time = beat + delay;
      const events = this.events.filter(event => event.juggler == jugglerIndex && event.time == time);
      if (rhythm.isSyncStandard() && beat % 2 == 1) {
        events.forEach(event => this.removeEvent(event));
      } else if (rhythm.isAsync()) {
        const hand = initialHand ^ (beat % 2);
        if (events.length > 1) {
          events.filter(event => event.hand != hand).forEach(event => this.removeEvent(event));
        } else if (events.length == 1) {
          events[0].hand = hand;
        }
      }
    }
    this.addMissingEvents(jugglerIndex);
    this.updateEventLinks();
  }

  setEventTime(event, time) {
    const delta = time - event.time;
    for (const otherEvent of this.events) {
      if (otherEvent.toEvent == event) {
        otherEvent.duration += delta;
      }
    }
    event.time = time;
    event.duration -= delta;
  }

  clearPattern() {
    this.removeEvents(() => true);
    this.numBeats = 0;
    this.jugglers = [];
  }

  removeEvents(predicate) {
    const eventsToRemove = this.events.filter(predicate);
    eventsToRemove.forEach(event => this.removeEvent(event));
    this.updateEventLinks();
  }

  removeEvent(event) {
    // NOTE: This may put the pattern into an invalid state if the event is the target of another event
    // updateEventLinks() should be called after removing events
    event.removeThrow();
    const index = this.events.indexOf(event);
    if (index > -1) {
      this.events.splice(index, 1);
    }
  }

  updateEventLinks() {
    // Recalculate the toEvent property for each event, possibly changing the toHand property to deal with handedness changing
    for (const event of this.events) {
      if (event.hasThrow()) {
        const landTime = (event.duration + event.time - 2) % this.numBeats;
        let best = undefined;
        for (const toEvent of this.events) {
          if (toEvent.juggler == event.toJuggler && Math.abs(toEvent.time - landTime) < 1e-6) {
            if (event.toHand == toEvent.hand) {
              best = toEvent;
            } else if (best == undefined) {
              best = toEvent;
            }
          }
        }
        if (best !== undefined) {
          event.toEvent = best;
          event.toHand = best.hand;
        } else {
          event.removeThrow();
        }
      }
    }
    this.validate();
  }

  renameJuggler(juggler, newId) {
    function convertJugglerId(id) {
      if (id == juggler) {
        return newId;
      }
      if (juggler < newId) {
        if (juggler <= id && id <= newId) {
          return id - 1;
        }
        return id;
      }
      if (newId <= id && id <= juggler) {
        return id + 1;
      }
      return id;
    }
    for (const j of this.jugglers) {
      j.id = convertJugglerId(j.id);
    }
    for (const event of this.events) {
      event.juggler = convertJugglerId(event.juggler);
      event.toJuggler = convertJugglerId(event.toJuggler);
    }
    this.jugglers.sort((a, b) => a.id - b.id);
  }

  validate() {
    this.events.forEach(event => {event.incoming = 0;});
    this.events.filter(event => event.toEvent !== undefined).forEach(event => {event.toEvent.incoming++;});
    this.valid = this.events.every(event => event.isValid());
  }
}
